import React, { useState, useRef, useCallback, useEffect } from 'react';

interface CarouselItem {
  id: number;
  imageUrl: string;
  title: string;
}

const RotatingCarousel: React.FC = () => {
  const [currentRotation, setCurrentRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragStartRotation, setDragStartRotation] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoRotateTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const RADIUS = 317;
  const PANEL_COUNT = 3;
  const PANEL_ANGLE = 360 / PANEL_COUNT;
  const AUTO_ROTATION_SPEED = 5000; // Time in ms for a full panel rotation
  
  const carouselItems: CarouselItem[] = [
    { 
      id: 1, 
      imageUrl: "/images/menu_recoil.png",
      title: "Recoil"
    },
    { 
      id: 2, 
      imageUrl: "/images/menu_misc.png",
      title: "Misc"
    },
    { 
      id: 3, 
      imageUrl: "/images/menu_settings.png",
      title: "Settings"
    }
  ];
  
  const mapRange = (value: number, inMin: number, inMax: number, outMin: number, outMax: number): number => {
    return (value - inMin) * (outMax - outMin) / (inMax - inMin) + outMin;
  };
  
  const snapToNearest = (rotation: number): number => {
    const snapAngle = PANEL_ANGLE;
    return Math.round(rotation / snapAngle) * snapAngle;
  };
  
  const updatePositions = useCallback((rotation: number) => {
    if (!carouselRef.current) return;
    
    const items = carouselRef.current.querySelectorAll('.rotating-item');
    
    items.forEach((item, index) => {
      const theta = ((rotation / 180 * Math.PI) + (index * (2 * Math.PI / PANEL_COUNT)));
      
      const x = Math.sin(theta) * RADIUS;
      const z = Math.cos(theta) * RADIUS;
      const scale = mapRange(z, -RADIUS, RADIUS, 0.6, 1);
      const opacity = mapRange(z, -RADIUS, RADIUS, 0.2, 1);
      
      // Apply transition only during auto-rotation
      if (isAutoRotating && !isDragging) {
        (item as HTMLElement).style.transition = 'transform 0.8s ease-in-out, opacity 0.8s ease-in-out, filter 0.8s ease-in-out';
      } else {
        (item as HTMLElement).style.transition = 'none';
      }
      
      (item as HTMLElement).style.transform = `
        translate(-50%, -50%)
        translateX(${x}px)
        translateZ(${z}px)
        rotateY(${-theta * (180 / Math.PI)}deg)
        scale(${scale})
      `;
      
      (item as HTMLElement).style.opacity = opacity.toString();
      (item as HTMLElement).style.filter = z < 0 ? `blur(${mapRange(z, -RADIUS, 0, 2, 0)}px)` : 'none';
      
      if (z > 0) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  }, [PANEL_COUNT, RADIUS, isDragging, isAutoRotating]);
  
  useEffect(() => {
    updatePositions(currentRotation);
  }, [currentRotation, updatePositions]);
  
  // Setup auto-rotation
  const startAutoRotation = useCallback(() => {
    if (autoRotateTimerRef.current) {
      clearInterval(autoRotateTimerRef.current);
    }
    
    autoRotateTimerRef.current = setInterval(() => {
      if (!isDragging && !isHovering) {
        setIsAutoRotating(true);
        setCurrentRotation(prev => {
          const newRotation = snapToNearest(prev + PANEL_ANGLE);
          return newRotation;
        });
        
        // Reset auto-rotating flag after transition completes
        setTimeout(() => {
          setIsAutoRotating(false);
        }, 800);
      }
    }, AUTO_ROTATION_SPEED);
  }, [isDragging, isHovering]);
  
  // Mouse events
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setIsAutoRotating(false);
    setStartX(e.clientX);
    setDragStartRotation(currentRotation);
  }, [currentRotation]);
  
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - startX;
    const newRotation = dragStartRotation + (deltaX * 0.3);
    updatePositions(newRotation);
  }, [isDragging, startX, dragStartRotation, updatePositions]);
  
  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const deltaX = e.clientX - startX;
    const newRotation = snapToNearest(dragStartRotation + (deltaX * 0.3));
    setCurrentRotation(newRotation);
  }, [isDragging, startX, dragStartRotation]);
  
  // Touch events
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    setIsAutoRotating(false);
    setStartX(e.touches[0].clientX);
    setDragStartRotation(currentRotation);
  }, [currentRotation]);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const deltaX = e.touches[0].clientX - startX;
    const newRotation = dragStartRotation + (deltaX * 0.3);
    updatePositions(newRotation);
  }, [isDragging, startX, dragStartRotation, updatePositions]);
  
  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // We don't have access to the touch position in the onTouchEnd event,
    // so we'll snap to the nearest panel based on the current rotation
    const newRotation = snapToNearest(currentRotation);
    setCurrentRotation(newRotation);
  }, [isDragging, currentRotation]);
  
  // Setup event listeners and auto-rotation
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    startAutoRotation();
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (autoRotateTimerRef.current) {
        clearInterval(autoRotateTimerRef.current);
      }
    };
  }, [handleMouseMove, handleMouseUp, startAutoRotation]);
  
  return (
    <div 
      ref={carouselRef} 
      className="absolute w-full h-full origin-center cursor-grab"
      style={{ transformStyle: 'preserve-3d' }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {carouselItems.map((item) => (
        <div 
          key={item.id}
          className="rotating-item absolute w-[414px] h-[276px] left-1/2 top-1/2 origin-center rounded-lg shadow-lg border border-blue-400/20 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(17, 24, 39, 0.7) 0%, rgba(55, 65, 81, 0.8) 100%)",
            boxShadow: "0 0 20px rgba(59, 130, 246, 0.4), 0 0 40px rgba(29, 78, 216, 0.2)",
            backfaceVisibility: 'visible'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="w-full h-full flex items-center justify-center">
              <img 
                src={item.imageUrl} 
                alt={item.title}
                style={{ 
                  width: "calc(100%)",
                  height: "calc(100%)"
                }}
                className="pointer-events-none"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RotatingCarousel; 